import React, { useEffect, useState } from "react";
import { Dialog, DialogContent } from "../../../../shadcn/components/ui/dialog";
import { DateTime } from "luxon";
import {
  amountFormatter,
  getAuctionEventById,
  getAllBiddersForEvent,
  getAllBidsForBanker,
  getLatestBid,
  updateSaleCert25Generated,
  updateSaleCert100Generated,
  updateAuctionEventStatus,
} from "services";
import {
  FileDown,
  FileText,
  X,
  Check,
  XIcon,
  CirclePause,
  Download,
  CheckCircle2,
  PenTool,
} from "lucide-react";
import { Button } from "../../../../shadcn/components/ui/button";
import { generateHighestBidderIntimationPDF } from "./HighestBidderIntimationPDF";
import { generateSaleCertificate25PerPDF } from "./SaleCertificate25PerPDF";
import { generateSaleCertificate100PerPDF } from "./SaleCertificate100PerPDF";
import { useSelector } from "react-redux";
import { Checkbox } from "../../../../shadcn/components/ui/checkbox";

interface ViewDetailModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  eventData: any;
  router: any;
}

export default function ViewDetailModal({
  open,
  onOpenChange,
  eventData,
  router,
}: ViewDetailModalProps) {
  const [fullEventData, setFullEventData] = useState<any>(null);
  const [bidders, setBidders] = useState<any[]>([]);
  const [bids, setBids] = useState<any[]>([]);
  const [highestBid, setHighestBid] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [reasonText, setReasonText] = useState("");
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [confirmationAction, setConfirmationAction] = useState<
    "approve" | "reject" | "hold" | null
  >(null);
  const [confirmationReason, setConfirmationReason] = useState("");
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [showSaleCertModal, setShowSaleCertModal] = useState(false);
  const [saleCertType, setSaleCertType] = useState<"25" | "100" | null>(null);
  const [saleCertEventUuid, setSaleCertEventUuid] = useState<string | null>(
    null,
  );
  const [isUpdatingSaleCert, setIsUpdatingSaleCert] = useState(false);

  const bank = useSelector((state: any) => {
    return state.bankerAuth.bank;
  });

  useEffect(() => {
    if (open && eventData?.listing_id) {
      fetchEventDetails();
    }
  }, [open, eventData]);

  const fetchEventDetails = async () => {
    if (!eventData?.listing_id) return;
    setIsLoading(true);
    try {
      // Fetch full event data
      const eventDetails: any = await getAuctionEventById(eventData.listing_id);
      setFullEventData(eventDetails);

      if (eventDetails?.uuid) {
        // Fetch bidders
        try {
          const biddersData: any = await getAllBiddersForEvent(
            eventDetails.uuid,
          );
          setBidders(biddersData || []);
        } catch (err) {
          console.error("Error fetching bidders:", err);
          setBidders([]);
        }

        // Fetch bids
        try {
          const bidsData: any = await getAllBidsForBanker(eventDetails.uuid);
          setBids(bidsData || []);
        } catch (err) {
          console.error("Error fetching bids:", err);
          setBids([]);
        }

        // Fetch highest bid
        try {
          const latestBid: any = await getLatestBid(eventDetails.uuid);
          setHighestBid(latestBid);
        } catch (err) {
          console.error("Error fetching highest bid:", err);
          setHighestBid(null);
        }
      }
    } catch (error) {
      console.error("Error fetching event details:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDateTime = (dateTime: string) => {
    if (!dateTime) return "N/A";
    return DateTime.fromISO(dateTime)
      .setZone("Asia/Kolkata")
      .toFormat("dd MMM yyyy, hh:mm a");
  };

  const formatDate = (dateTime: string) => {
    if (!dateTime) return "N/A";
    return DateTime.fromISO(dateTime)
      .setZone("Asia/Kolkata")
      .toFormat("dd MMM yyyy");
  };

  const formatTime = (dateTime: string) => {
    if (!dateTime) return "N/A";
    return DateTime.fromISO(dateTime)
      .setZone("Asia/Kolkata")
      .toFormat("hh:mm a");
  };

  const formatFinalEndTime = (dateTime: string) => {
    if (!dateTime) return "N/A";
    return DateTime.fromMillis(parseInt(dateTime))
      .setZone("Asia/Kolkata")
      .toFormat("hh:mm a");
  };

  const formatBidTime = (timestamp: any) => {
    if (!timestamp) return "N/A";
    const ts = typeof timestamp === "string" ? parseInt(timestamp) : timestamp;
    return DateTime.fromMillis(ts).setZone("Asia/Kolkata").toFormat("hh:mm a");
  };

  const handleSaleCert25GeneratedChange = (
    checked: boolean,
    eventUuid: string,
  ) => {
    if (!checked || !eventUuid) return; // Only allow setting to true
    setSaleCertType("25");
    setSaleCertEventUuid(eventUuid);
    setShowSaleCertModal(true);
  };

  const handleSaleCert100GeneratedChange = (
    checked: boolean,
    eventUuid: string,
  ) => {
    if (!checked || !eventUuid) return; // Only allow setting to true
    setSaleCertType("100");
    setSaleCertEventUuid(eventUuid);
    setShowSaleCertModal(true);
  };

  const handleConfirmSaleCert = async () => {
    if (!saleCertEventUuid || !saleCertType) return;

    setIsUpdatingSaleCert(true);
    try {
      if (saleCertType === "25") {
        await updateSaleCert25Generated(saleCertEventUuid, true);
        // Update local state
        if (fullEventData) {
          setFullEventData({
            ...fullEventData,
            sale_cert_25_generated: true,
          });
        }
      } else {
        await updateSaleCert100Generated(saleCertEventUuid, true);
        // Update local state
        if (fullEventData) {
          setFullEventData({
            ...fullEventData,
            sale_cert_100_generated: true,
          });
        }
      }

      // Close modal and reset state
      setShowSaleCertModal(false);
      setSaleCertType(null);
      setSaleCertEventUuid(null);
    } catch (error) {
      console.error(
        `Error updating sale_cert_${saleCertType}_generated:`,
        error,
      );
      alert("Failed to update sale certificate status. Please try again.");
    } finally {
      setIsUpdatingSaleCert(false);
    }
  };

  const handleApproveClick = () => {
    setConfirmationAction("approve");
    setConfirmationReason("");
    setShowConfirmationModal(true);
  };

  const handleRejectClick = () => {
    setConfirmationAction("reject");
    setConfirmationReason("");
    setShowConfirmationModal(true);
  };

  const handleHoldClick = () => {
    setConfirmationAction("hold");
    setConfirmationReason("");
    setShowConfirmationModal(true);
  };

  const handleConfirmAction = async () => {
    const event = fullEventData || eventData;
    if (!event?.uuid) return;

    // Validate reason for reject and hold
    if (
      (confirmationAction === "reject" || confirmationAction === "hold") &&
      !confirmationReason.trim()
    ) {
      alert("Please provide a reason for this action.");
      return;
    }

    setIsUpdatingStatus(true);
    try {
      let status = "";
      let comment = "";

      switch (confirmationAction) {
        case "approve":
          status = "success";
          comment = "";
          break;
        case "reject":
          status = "bid_rejected";
          comment = confirmationReason.trim();
          break;
        case "hold":
          status = "holding_bid_approval";
          comment = confirmationReason.trim();
          break;
      }

      await updateAuctionEventStatus(event.uuid, status, comment);

      // Update local state
      if (fullEventData) {
        setFullEventData({
          ...fullEventData,
          status: status,
          comment: comment,
        });
      }

      // Close modal and reset state
      setShowConfirmationModal(false);
      setConfirmationAction(null);
      setConfirmationReason("");
      setReasonText("");

      // Refresh event details
      await fetchEventDetails();
    } catch (error) {
      console.error("Error updating event status:", error);
      alert("Failed to update event status. Please try again.");
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case "accepted":
        return "bg-[#e0f2fe] text-[#075985]";
      case "live":
        return "bg-[rgba(34,197,94,0.14)] text-[#15803d]";
      case "end":
      case "success":
        return "bg-[rgba(22,163,74,0.16)] text-[#166534]";
      case "cancelled":
      case "failed":
      case "bid_rejected":
      case "holding_bid_approval":
        return "bg-[rgba(248,113,113,0.18)] text-[#b91c1c]";
      case "pending_bid_approval":
        return "bg-yellow-100 text-yellow-700";
      default:
        return "bg-gray-200 text-gray-800";
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status?.toLowerCase()) {
      case "accepted":
        return "Upcoming";
      case "end":
        return "Ended";
      case "success":
        return "Success";
      case "pending_bid_approval":
        return "To Be Approved";
      case "bid_rejected":
        return "Bid Rejected";
      case "failed":
        return "Failed";
      case "holding_bid_approval":
        return "On Hold";
      case "cancelled":
        return "Cancelled";
      default:
        return status?.charAt(0).toUpperCase() + status?.slice(1) || "N/A";
    }
  };

  const event = fullEventData || eventData;
  if (!event) return null;

  const propertyDump = event?.property_dump || {};
  const propertyType = propertyDump?.sub_asset_type?.name || "N/A";
  const cityName = propertyDump?.city?.name || "N/A";
  const stateName = propertyDump?.state?.name || "N/A";
  const borrowerName = event?.borrower_name || "N/A";
  const bankName = propertyDump?.bank?.name || "N/A";
  const branchName = propertyDump?.branch?.name || "N/A";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-[1120px] w-[96vw] max-h-[92vh] overflow-hidden p-0 rounded-[20px] border border-gray-200 shadow-[0_22px_40px_rgba(15,23,42,0.3)]"
        removeIcon={true}
      >
        <div className="flex flex-col h-full max-h-[92vh]">
          {/* Modal Header */}
          <div className="flex justify-between items-start px-[18px] py-3 border-b border-gray-200 bg-[#f9fafb]">
            <div className="flex-1 min-w-0">
              <div className="text-base font-semibold text-gray-900 leading-tight">
                Auction Event ID –{" "}
                <span className="font-normal">{event.listing_id || "N/A"}</span>
              </div>
              <div className="text-xs text-gray-600 capitalize mt-0.5 leading-tight">
                {propertyType} - {cityName}, {stateName} (Borrower:{" "}
                {borrowerName})
              </div>
              <div className="flex flex-wrap gap-1.5 items-center mt-1.5">
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-medium capitalize leading-tight ${getStatusColor(
                    event.status,
                  )}`}
                >
                  {getStatusLabel(event.status)}
                </span>
                <span className="inline-flex items-center capitalize px-2 py-0.5 rounded-full text-[11px] font-medium text-gray-600 border border-dashed border-gray-300 bg-white">
                  {bankName} · {branchName}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2 ml-2.5 flex-shrink-0">
              <Button
                variant="ghost"
                className="h-auto px-3 py-1.5 text-xs font-medium rounded-full border border-gray-200 bg-white text-[#ff7a00] hover:bg-[rgba(255,122,0,0.08)] hover:border-[#ff7a00]"
                size="sm"
                disabled={!event.report_generated}
                onClick={() => {
                  router.push(event.pdfs[0]?.download_url);
                }}
              >
                <FileDown className="w-3.5 h-3.5 mr-1.5" />
                Auction Report
              </Button>
              <Button
                variant="ghost"
                className="h-auto px-3 py-1.5 text-xs font-medium rounded-full border border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                size="sm"
                disabled={event.status !== "success"}
                onClick={async () => {
                  try {
                    const event = fullEventData || eventData;
                    await generateHighestBidderIntimationPDF(
                      event,
                      highestBid,
                      bidders,
                      bank,
                    );
                  } catch (error) {
                    console.error("Error generating PDF:", error);
                    alert("Failed to generate PDF. Please try again.");
                  }
                }}
              >
                <FileText className="w-3.5 h-3.5 mr-1.5" />
                Highest Bidder Letter
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 rounded-full bg-gray-200 hover:bg-gray-300"
                onClick={() => onOpenChange(false)}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Modal Body */}
          <div className="flex-1 overflow-y-auto p-3 bg-gray-50">
            {isLoading ? (
              <div className="flex items-center justify-center h-64">
                <div className="text-gray-500 text-sm">
                  Loading event details...
                </div>
              </div>
            ) : (
              <>
                {/* Auction Summary */}
                <div className="bg-white rounded-[14px] border border-gray-200 px-3 py-2.5 mb-3">
                  <div className="flex justify-between items-center mb-1.5">
                    <div className="text-[13px] font-semibold text-gray-900">
                      Auction Summary
                    </div>
                    <div className="text-[11px] text-gray-500">
                      {event.start_date_time && event.end_date_time
                        ? `${formatDate(event.start_date_time)} | ${formatTime(
                            event.start_date_time,
                          )} (IST) - ${formatDate(
                            event.end_date_time,
                          )} | ${formatTime(event.end_date_time)} (IST)`
                        : "—"}
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {/* Row 1 */}
                    <div className="rounded-[10px] bg-[#f9fafb] px-2 py-1.5">
                      <div className="text-[11px] text-gray-500 mb-0.5">
                        Reserve Price
                      </div>
                      <div className="text-[13px] font-semibold text-gray-900">
                        {event.reserve_price || propertyDump?.reserve_price
                          ? `₹${amountFormatter(
                              parseFloat(
                                event.reserve_price ||
                                  propertyDump?.reserve_price,
                              ),
                            )}`
                          : "—"}
                      </div>
                    </div>
                    <div className="rounded-[10px] bg-[#f9fafb] px-2 py-1.5">
                      <div className="text-[11px] text-gray-500 mb-0.5">
                        Highest Bid
                      </div>
                      <div className="text-[13px] font-semibold text-gray-900">
                        {highestBid?.final_amount
                          ? `₹${amountFormatter(
                              parseFloat(highestBid.final_amount),
                            )}`
                          : "—"}
                      </div>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-1.5">
                      <div className="rounded-[10px] bg-[#f9fafb] px-2 py-1.5 flex justify-left items-center gap-1.5 w-full ">
                        <div className="flex flex-col items-center">
                          <div className="text-[11px] text-gray-500">
                            Applied Bidders
                          </div>
                          <div className="text-[13px] font-semibold text-gray-900">
                            {bidders.filter(
                              (bidder: any) =>
                                bidder?.access_type === "primary",
                            )?.length || 0}
                          </div>
                        </div>
                      </div>
                      <div className="rounded-[10px] bg-[#f9fafb] px-2 py-1.5 flex justify-left items-center gap-1.5 w-full">
                        <div className="flex flex-col items-center">
                          <div className="text-[11px] text-gray-500">
                            Qualified Bidders
                          </div>
                          <div className="text-[13px] font-semibold text-gray-900">
                            {bidders
                              .filter(
                                (bidder: any) =>
                                  bidder?.access_type === "primary",
                              )
                              ?.filter((bidder) => bidder.status === "verified")
                              .length || 0}
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* Row 2 */}
                    <div className="rounded-[10px] bg-[#f9fafb] px-2 py-1.5">
                      <div className="text-[11px] text-gray-500 mb-0.5">
                        Auction Window
                      </div>
                      <div className="text-[13px] font-semibold text-gray-900">
                        {event.start_date_time && event.end_date_time
                          ? `${formatDate(event.start_date_time)}, ${formatTime(
                              event.start_date_time,
                            )} - ${formatFinalEndTime(
                              event.final_end_date,
                            )} (IST)`
                          : "—"}
                      </div>
                    </div>
                    <div className="rounded-[10px] bg-[#f9fafb] px-2 py-1.5">
                      <div className="text-[11px] text-gray-500 mb-0.5">
                        Extensions
                      </div>
                      <div className="text-[13px] font-semibold text-gray-900">
                        {event.number_of_extension || 0}
                      </div>
                    </div>
                    <div className="rounded-[10px] bg-[#f9fafb] px-2 py-1.5">
                      <div className="text-[11px] text-gray-500 mb-0.5">
                        Event Status
                      </div>
                      <div className={`text-[13px] font-semibold`}>
                        {getStatusLabel(event.status)}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Main Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                  {/* Left Column */}
                  <div className="space-y-3">
                    {/* Event & Property Details */}
                    <div className="bg-white rounded-[14px] border border-gray-200 px-3 py-2.5">
                      <div className="flex justify-between items-center mb-1.5">
                        <div className="text-[13px] font-semibold text-gray-900">
                          Event & Property Details
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-x-3.5 gap-y-1.5 text-xs">
                        <div>
                          <div className="text-[11px] text-gray-500 mb-0.5">
                            Approver
                          </div>
                          <div className="text-xs text-gray-900">
                            {event.meta?.approver?.[0]?.name ||
                              event.meta?.approver?.[0]?.email ||
                              "Auction Bazaar"}
                          </div>
                        </div>
                        <div>
                          <div className="text-[11px] text-gray-500 mb-0.5">
                            Possession Type
                          </div>
                          <div className="text-xs text-gray-900">
                            {event.possession_type ||
                              propertyDump?.possession_type ||
                              "N/A"}
                          </div>
                        </div>
                        <div>
                          <div className="text-[11px] text-gray-500 mb-0.5">
                            Inspection Window
                          </div>
                          <div className="text-xs text-gray-900">
                            {propertyDump?.physical_inspection_timeline ||
                              "N/A"}
                          </div>
                        </div>
                        <div>
                          <div className="text-[11px] text-gray-500 mb-0.5">
                            Event Type
                          </div>
                          <div className="text-xs text-gray-900">
                            {event.event_type || "N/A"}
                          </div>
                        </div>
                        <div>
                          <div className="text-[11px] text-gray-500 mb-0.5">
                            KYC Type
                          </div>
                          <div className="text-xs text-gray-900">
                            {event.kyc_type || propertyDump?.kyc_type || "N/A"}
                          </div>
                        </div>
                        <div>
                          <div className="text-[11px] text-gray-500 mb-0.5">
                            Inspection Officer Contact
                          </div>
                          <div className="text-xs text-gray-900">
                            {propertyDump?.contact || "N/A"}
                          </div>
                        </div>
                      </div>
                      <div className="h-px bg-[#f3f4f6] my-1.5"></div>
                      <div>
                        <div className="text-[11px] text-gray-500 mb-0.5">
                          Property Address
                        </div>
                        <div className="text-[11px] text-gray-900 mt-0.5">
                          {propertyDump?.address || "N/A"}
                        </div>
                      </div>
                    </div>

                    {/* EMD & Payment Details */}
                    <div className="bg-white rounded-[14px] border border-gray-200 px-3 py-2.5">
                      <div className="flex justify-between items-center mb-1.5">
                        <div className="text-[13px] font-semibold text-gray-900">
                          EMD & Payment Details
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-x-3.5 gap-y-1.5 text-xs">
                        <div>
                          <div className="text-[11px] text-gray-500 mb-0.5">
                            EMD
                          </div>
                          <div className="text-xs text-gray-900">
                            {propertyDump?.emd_amount
                              ? `₹${amountFormatter(
                                  parseFloat(propertyDump.emd_amount),
                                )}`
                              : "N/A"}
                          </div>
                        </div>
                        <div>
                          <div className="text-[11px] text-gray-500 mb-0.5">
                            EMD Last Date
                          </div>
                          <div className="text-xs text-gray-900">
                            {propertyDump?.emd_last_date
                              ? formatDateTime(propertyDump.emd_last_date)
                              : "N/A"}
                          </div>
                        </div>
                        <div>
                          <div className="text-[11px] text-gray-500 mb-0.5">
                            Account Name
                          </div>
                          <div className="text-xs text-gray-900">
                            {event.emd_payment_account_name || "N/A"}
                          </div>
                        </div>
                        <div>
                          <div className="text-[11px] text-gray-500 mb-0.5">
                            Account No.
                          </div>
                          <div className="text-xs text-gray-900">
                            {event.emd_payment_account_number || "N/A"}
                          </div>
                        </div>
                        <div>
                          <div className="text-[11px] text-gray-500 mb-0.5">
                            IFSC
                          </div>
                          <div className="text-xs text-gray-900">
                            {event.emd_payment_account_ifsc || "N/A"}
                          </div>
                        </div>
                        <div>
                          <div className="text-[11px] text-gray-500 mb-0.5">
                            Method
                          </div>
                          <div className="text-xs text-gray-900">
                            {event.emd_payment_method || "N/A"}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Right Column */}
                  <div className="space-y-3 lg:col-span-2">
                    {/* Bidders */}
                    <div className="bg-white rounded-[14px] border border-gray-200 px-3 py-2.5">
                      <div className="flex justify-between items-center mb-1.5">
                        <div className="text-[13px] font-semibold text-gray-900">
                          Bidders
                        </div>
                        <div className="text-[11px] text-gray-500">
                          <Button
                            className="text-xs p-3 h-7"
                            disabled={[
                              "end",
                              "rejected",
                              "cancelled",
                              "pending_bid_approval",
                              "holding_bid_approval",
                              "success",
                              "failed",
                              "bid_rejected",
                            ].includes(event?.status)}
                            onClick={() => {
                              router.push(
                                `/dashboard/bidders?for_event=${event.listing_id}`,
                              );
                            }}
                          >
                            Add Bidders
                          </Button>
                        </div>
                      </div>
                      {bidders.length > 0 ? (
                        <div className="rounded-[10px] border border-gray-200 overflow-hidden">
                          <div className="max-h-[264px] overflow-y-auto">
                            <table className="w-full text-xs border-collapse">
                              <thead className="bg-[#f9fafb] sticky top-0 z-10">
                                <tr>
                                  <th className="px-1.5 py-1.5 text-left text-xs text-gray-600 font-semibold border-b border-[#f3f4f6] bg-[#f9fafb]">
                                    Email
                                  </th>
                                  <th className="px-1.5 py-1.5 text-left text-xs text-gray-600 font-semibold border-b border-[#f3f4f6] bg-[#f9fafb]">
                                    KYC
                                  </th>
                                  <th className="px-1.5 py-1.5 text-left text-xs text-gray-600 font-semibold border-b border-[#f3f4f6] bg-[#f9fafb]">
                                    EMD
                                  </th>
                                  <th className="px-1.5 py-1.5 text-left text-xs text-gray-600 font-semibold border-b border-[#f3f4f6] bg-[#f9fafb]">
                                    Agreement
                                  </th>
                                  <th className="px-1.5 py-1.5 text-left text-xs text-gray-600 font-semibold border-b border-[#f3f4f6] bg-[#f9fafb]">
                                    Application
                                  </th>
                                  <th className="px-1.5 py-1.5 text-left text-xs text-gray-600 font-semibold border-b border-[#f3f4f6] bg-[#f9fafb]">
                                    KYC Type
                                  </th>
                                  <th className="px-1.5 py-1.5 text-left text-xs text-gray-600 font-semibold border-b border-[#f3f4f6] bg-[#f9fafb]">
                                    Status
                                  </th>
                                  <th className="px-1.5 py-1.5 text-left text-xs text-gray-600 font-semibold border-b border-[#f3f4f6] bg-[#f9fafb]">
                                    Jt. Bidders
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {bidders
                                  ?.filter(
                                    (bidder: any) =>
                                      bidder?.access_type === "primary",
                                  )
                                  .map((bidder: any, idx: number) => (
                                    <tr
                                      key={bidder?.id || idx}
                                      className="border-b border-[#f3f4f6]"
                                    >
                                      <td className="px-1.5 py-1.5 text-gray-900 text-xs">
                                        <button
                                          onClick={() => {
                                            let bidderType = "individual";

                                            if (bidder?.is_joint) {
                                              bidderType = "joint";
                                            } else if (
                                              [
                                                "company",
                                                "proprietors",
                                                "partnership",
                                              ].includes(
                                                bidder?.meta?.user_details
                                                  ?.user_type?.value,
                                              )
                                            ) {
                                              bidderType =
                                                bidder?.meta?.user_details
                                                  ?.user_type?.value;
                                            }

                                            router.push(
                                              `/dashboard/bidders/view/?id=${bidder?.uuid}&bidder_type=${bidderType}`,
                                            );
                                          }}
                                          className="text-blue-600 hover:underline"
                                        >
                                          {bidder?.email || "N/A"}
                                        </button>
                                      </td>

                                      <td className="px-1.5 py-1.5">
                                        <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[11px] font-medium bg-[#dcfce7] text-[#047857]">
                                          {bidder?.kyc_verified
                                            ? "Verified"
                                            : "Not Verified"}
                                        </span>
                                      </td>

                                      <td className="px-1.5 py-1.5 text-gray-900 text-xs">
                                        {bidder?.emd_amount_paid ? "Yes" : "No"}
                                      </td>

                                      <td className="px-1.5 py-1.5 text-gray-900 text-xs">
                                        {bidder?.agreement_signed
                                          ? "Yes"
                                          : "No"}
                                      </td>

                                      <td className="px-1.5 py-1.5 text-gray-900 text-xs">
                                        {bidder?.application_form_filled
                                          ? "Yes"
                                          : "No"}
                                      </td>

                                      <td className="px-1.5 py-1.5 text-gray-900 text-xs">
                                        {event?.kyc_type || "N/A"}
                                      </td>

                                      <td className="px-1.5 py-1.5">
                                        <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[11px] font-medium bg-[#dcfce7] text-[#047857]">
                                          {bidder?.status || "Not Verified"}
                                        </span>
                                      </td>

                                      <td className="px-1.5 py-1.5 text-gray-900 text-xs">
                                        {bidder?.bidder_relations?.length || 0}
                                      </td>
                                    </tr>
                                  ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      ) : (
                        <div className="text-xs text-gray-500 mt-1">
                          No bidders registered for this auction.
                        </div>
                      )}
                    </div>

                    {/* Bid Details & Approval */}
                    <div className="bg-white rounded-[14px] border border-gray-200 px-3 py-2.5">
                      <div className="flex justify-between items-center mb-1.5">
                        <div className="text-[13px] font-semibold text-gray-900">
                          Bid Details & Approval
                        </div>
                      </div>
                      {bids.length > 0 ? (
                        <div className="rounded-[10px] border border-gray-200 overflow-hidden mb-2">
                          <div className="max-h-[264px] overflow-y-auto">
                            <table className="w-full text-xs border-collapse">
                              <thead className="bg-[#f9fafb] sticky top-0 z-10">
                                <tr>
                                  <th className="px-1.5 py-1.5 text-left text-xs text-gray-600 font-semibold border-b border-[#f3f4f6] w-8 bg-[#f9fafb]"></th>
                                  <th className="px-1.5 py-1.5 text-left text-xs text-gray-600 font-semibold border-b border-[#f3f4f6] bg-[#f9fafb]">
                                    Bidder
                                  </th>
                                  <th className="px-1.5 py-1.5 text-left text-xs text-gray-600 font-semibold border-b border-[#f3f4f6] bg-[#f9fafb]">
                                    Bid Amount
                                  </th>
                                  <th className="px-1.5 py-1.5 text-left text-xs text-gray-600 font-semibold border-b border-[#f3f4f6] bg-[#f9fafb]">
                                    Bid Time
                                  </th>
                                  <th className="px-1.5 py-1.5 text-left text-xs text-gray-600 font-semibold border-b border-[#f3f4f6] bg-[#f9fafb]">
                                    Status
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {bids
                                  .sort(
                                    (a: any, b: any) =>
                                      parseFloat(b.final_amount || 0) -
                                      parseFloat(a.final_amount || 0),
                                  )
                                  .map((bid: any, idx: number) => {
                                    const isHighest = idx === 0;
                                    return (
                                      <tr
                                        key={idx}
                                        className="border-b border-[#f3f4f6]"
                                      >
                                        <td className="px-1.5 py-1.5">
                                          <input
                                            type="radio"
                                            name="selectedBid"
                                            defaultChecked={isHighest}
                                            disabled={!isHighest}
                                            className="w-3 h-3 text-orange-600 focus:ring-orange-500"
                                          />
                                        </td>
                                        <td className="px-1.5 py-1.5 text-gray-900 text-xs">
                                          {bid.meta?.email ||
                                            bid.bidder_name ||
                                            bid.user_email ||
                                            "N/A"}
                                        </td>
                                        <td className="px-1.5 py-1.5 text-gray-900 font-medium text-xs">
                                          ₹
                                          {amountFormatter(
                                            parseFloat(bid.final_amount || 0),
                                          )}
                                        </td>
                                        <td className="px-1.5 py-1.5 text-gray-900 text-xs">
                                          {bid.meta?.initial_bid !== true
                                            ? formatBidTime(bid.frontend_timestamp)
                                            : "-"}
                                        </td>
                                        <td className="px-1.5 py-1.5">
                                          {isHighest ? (
                                            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[11px] font-medium bg-[#dcfce7] text-[#047857]">
                                              Highest
                                            </span>
                                          ) : (
                                            <>
                                              {eventData.event_type ===
                                                "Asset transfer" &&
                                              bid.meta?.initial_bid === true ? (
                                                <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[11px] font-medium bg-[rgba(248,113,113,0.18)] text-[#b91c1c]">
                                                  Offer value
                                                </span>
                                              ) : (
                                                <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[11px] font-medium bg-[rgba(248,113,113,0.18)] text-[#b91c1c]">
                                                  Outbid
                                                </span>
                                              )}
                                            </>
                                          )}
                                        </td>
                                      </tr>
                                    );
                                  })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      ) : (
                        <div className="text-xs text-gray-500 mb-2">
                          No bids received for this event.
                        </div>
                      )}

                      <div className="flex flex-wrap gap-2 mb-3">
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-auto px-3 py-1.5 text-xs font-medium rounded-full border border-[#16a34a] text-[#16a34a] hover:bg-green-50"
                          disabled={
                            event.status !== "pending_bid_approval" ||
                            isUpdatingStatus
                          }
                          onClick={handleApproveClick}
                        >
                          <Check className="w-3 h-3 mr-1" />
                          Approve Highest Bid
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-auto px-3 py-1.5 text-xs font-medium rounded-full border border-[#dc2626] text-[#dc2626] hover:bg-red-50"
                          disabled={
                            event.status !== "pending_bid_approval" ||
                            isUpdatingStatus
                          }
                          onClick={handleRejectClick}
                        >
                          <XIcon className="w-3 h-3 mr-1" />
                          Reject Auction
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-auto px-3 py-1.5 text-xs font-medium rounded-full border border-[#ea580c] text-[#ea580c] hover:bg-orange-50"
                          disabled={
                            event.status !== "pending_bid_approval" ||
                            isUpdatingStatus
                          }
                          onClick={handleHoldClick}
                        >
                          <CirclePause className="w-3 h-3 mr-1" />
                          Keep Result on Hold
                        </Button>
                      </div>
                      {["bid_rejected", "holding_bid_approval"].includes(
                        event.status,
                      ) && (
                        <div className="mt-2">
                          <div className="text-xs text-gray-500 mb-1 font-medium">
                            Reason (for Reject / Hold)
                          </div>
                          <textarea
                            value={event.comment || ""}
                            disabled={event.status !== "pending_bid_approval"}
                            placeholder="Enter detailed reason to reject or keep the auction result on hold."
                            className="w-full min-h-[56px] rounded-[10px] border border-gray-200 p-2 text-xs resize-y focus:outline-none focus:ring-2 focus:ring-orange-500"
                          />
                        </div>
                      )}
                    </div>

                    {/* Post Auction Documents */}
                    <div className="bg-white rounded-[18px] border border-gray-200 p-4 shadow-[0_10px_20px_rgba(15,23,42,0.03)]">
                      <div className="flex justify-between items-center mb-3">
                        <div className="text-sm font-semibold text-gray-900">
                          Post Auction Documents
                        </div>
                      </div>

                      <div className="mb-3">
                        <div className="flex gap-3 items-center text-xs">
                          <label className="flex items-center gap-1.5 cursor-pointer">
                            <Checkbox
                              id={`sale-cert-25-received`}
                              checked={event.sale_cert_25_generated}
                              disabled={
                                event.status !== "success" ||
                                event.sale_cert_25_generated
                              }
                              onCheckedChange={(isChecked: boolean) => {
                                if (isChecked && event.uuid) {
                                  handleSaleCert25GeneratedChange(
                                    isChecked,
                                    event.uuid,
                                  );
                                }
                              }}
                            />
                            25% payment received
                          </label>
                          <label className="flex items-center gap-1.5 cursor-pointer">
                            <Checkbox
                              id={`sale-cert-100-received`}
                              checked={event.sale_cert_100_generated || false}
                              disabled={
                                !event.sale_cert_25_generated ||
                                event.sale_cert_100_generated
                              }
                              onCheckedChange={(isChecked: boolean) => {
                                if (isChecked && event.uuid) {
                                  handleSaleCert100GeneratedChange(
                                    isChecked,
                                    event.uuid,
                                  );
                                }
                              }}
                            />
                            100% payment received
                          </label>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 mt-2.5">
                        <div
                          className={`border border-dashed border-gray-200 rounded-[10px] p-2 text-xs ${
                            !event.report_generated
                              ? "opacity-50 pointer-events-none"
                              : ""
                          }`}
                        >
                          <div className="font-semibold text-gray-900 mb-1 text-xs">
                            1. Auction Report
                          </div>
                          <div className="text-[11px] text-gray-500 mb-2">
                            {event.report_generated
                              ? "Generated"
                              : "Not generated"}
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            className="w-full h-auto px-2 py-1 text-[11px] rounded-full border border-gray-200"
                            disabled={!event.report_generated}
                            onClick={() => {
                              router.push(event.pdfs[0]?.download_url);
                            }}
                          >
                            <Download className="w-3 h-3 mr-1" />
                            Download
                          </Button>
                        </div>
                        <div
                          className={`border border-dashed border-gray-200 rounded-[10px] p-2 text-xs ${
                            event.status !== "success"
                              ? "opacity-50 pointer-events-none"
                              : ""
                          }`}
                        >
                          <div className="font-semibold text-gray-900 mb-1 text-xs">
                            2. Highest Bidder Intimation
                          </div>
                          <div className="text-[11px] text-gray-500 mb-2">
                            {event.status === "success"
                              ? "Generated"
                              : "Not generated"}
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            className="w-full h-auto px-2 py-1 text-[11px] rounded-full border border-gray-200"
                            disabled={event.status !== "success"}
                            onClick={async () => {
                              try {
                                const event = fullEventData || eventData;
                                await generateHighestBidderIntimationPDF(
                                  event,
                                  highestBid,
                                  bidders,
                                  bank,
                                );
                              } catch (error) {
                                console.error("Error generating PDF:", error);
                                alert(
                                  "Failed to generate PDF. Please try again.",
                                );
                              }
                            }}
                          >
                            <Download className="w-3 h-3 mr-1" />
                            Download
                          </Button>
                        </div>
                        <div
                          className={`border border-dashed border-gray-200 rounded-[10px] p-2 text-xs ${
                            !event.sale_cert_25_generated
                              ? "opacity-50 pointer-events-none"
                              : ""
                          }`}
                        >
                          <div className="font-semibold text-gray-900 mb-1 text-xs">
                            3. Sale Confirmation (25%)
                          </div>
                          <div className="text-[11px] text-gray-500 mb-2">
                            {event.sale_cert_25_generated
                              ? "Generated"
                              : "Not generated"}
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            className="w-full h-auto px-2 py-1 text-[11px] rounded-full border border-gray-200"
                            disabled={!event.sale_cert_25_generated}
                            onClick={async () => {
                              try {
                                const event = fullEventData || eventData;
                                await generateSaleCertificate25PerPDF(
                                  event,
                                  highestBid,
                                  bidders,
                                  bank,
                                );
                              } catch (error) {
                                console.error("Error generating PDF:", error);
                                alert(
                                  "Failed to generate PDF. Please try again.",
                                );
                              }
                            }}
                          >
                            <Download className="w-3 h-3 mr-1" />
                            Download
                          </Button>
                        </div>
                        <div
                          className={`border border-dashed border-gray-200 rounded-[10px] p-2 text-xs ${
                            !event.sale_cert_100_generated
                              ? "opacity-50 pointer-events-none"
                              : ""
                          }`}
                        >
                          <div className="font-semibold text-gray-900 mb-1 text-xs">
                            4. Sale Certificate (100%)
                          </div>
                          <div className="text-[11px] text-gray-500 mb-2">
                            {event.sale_cert_100_generated
                              ? "Generated"
                              : "Not generated"}
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            className="w-full h-auto px-2 py-1 text-[11px] rounded-full border border-gray-200"
                            disabled={!event.sale_cert_100_generated}
                            onClick={async () => {
                              try {
                                const event = fullEventData || eventData;
                                await generateSaleCertificate100PerPDF(
                                  event,
                                  highestBid,
                                  bidders,
                                  bank,
                                );
                              } catch (error) {
                                console.error("Error generating PDF:", error);
                                alert(
                                  "Failed to generate PDF. Please try again.",
                                );
                              }
                            }}
                          >
                            <Download className="w-3 h-3 mr-1" />
                            Download
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </DialogContent>

      {/* Confirmation Modal */}
      <Dialog
        open={showConfirmationModal}
        onOpenChange={setShowConfirmationModal}
      >
        <DialogContent className="max-w-md w-[90vw] rounded-[20px] border border-gray-200 shadow-[0_22px_40px_rgba(15,23,42,0.3)]">
          <div className="p-4">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {confirmationAction === "approve"
                  ? "Approve Highest Bid"
                  : confirmationAction === "reject"
                  ? "Reject Auction"
                  : "Keep Result on Hold"}
              </h3>
              <p className="text-sm text-gray-600">
                {confirmationAction === "approve"
                  ? "Are you sure you want to approve the highest bid? This will mark the auction as successful."
                  : confirmationAction === "reject"
                  ? "Are you sure you want to reject this auction? Please provide a reason below."
                  : "Are you sure you want to keep the result on hold? Please provide a reason below."}
              </p>
            </div>

            {(confirmationAction === "reject" ||
              confirmationAction === "hold") && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={confirmationReason}
                  onChange={(e) => setConfirmationReason(e.target.value)}
                  placeholder="Enter detailed reason..."
                  className="w-full min-h-[100px] rounded-[10px] border border-gray-200 p-3 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-orange-500"
                  disabled={isUpdatingStatus}
                />
              </div>
            )}

            <div className="flex gap-3 justify-end">
              <Button
                variant="outline"
                size="sm"
                className="h-auto px-4 py-2 text-sm rounded-full"
                onClick={() => {
                  setShowConfirmationModal(false);
                  setConfirmationAction(null);
                  setConfirmationReason("");
                }}
                disabled={isUpdatingStatus}
              >
                Cancel
              </Button>
              <Button
                variant="outline"
                size="sm"
                className={`h-auto px-4 py-2 text-sm rounded-full ${
                  confirmationAction === "approve"
                    ? "border border-[#16a34a] text-[#16a34a] hover:bg-green-50"
                    : confirmationAction === "reject"
                    ? "border border-[#dc2626] text-[#dc2626] hover:bg-red-50"
                    : "border border-[#ea580c] text-[#ea580c] hover:bg-orange-50"
                }`}
                onClick={handleConfirmAction}
                disabled={
                  isUpdatingStatus ||
                  ((confirmationAction === "reject" ||
                    confirmationAction === "hold") &&
                    !confirmationReason.trim())
                }
              >
                {isUpdatingStatus
                  ? "Processing..."
                  : confirmationAction === "approve"
                  ? "Approve"
                  : confirmationAction === "reject"
                  ? "Reject"
                  : "Keep on Hold"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Sale Certificate Confirmation Modal */}
      <Dialog open={showSaleCertModal} onOpenChange={setShowSaleCertModal}>
        <DialogContent className="max-w-md w-[90vw] rounded-[20px] border border-gray-200 shadow-[0_22px_40px_rgba(15,23,42,0.3)]">
          <div className="p-4">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Confirm {saleCertType}% Payment Received
              </h3>
              <p className="text-sm text-gray-600">
                Are you sure you want to mark the {saleCertType}% payment as
                received? This will enable the {saleCertType}% Sale Certificate
                download section.
              </p>
            </div>

            <div className="flex gap-3 justify-end">
              <Button
                variant="outline"
                size="sm"
                className="h-auto px-4 py-2 text-sm rounded-full"
                onClick={() => {
                  setShowSaleCertModal(false);
                  setSaleCertType(null);
                  setSaleCertEventUuid(null);
                }}
                disabled={isUpdatingSaleCert}
              >
                Cancel
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-auto px-4 py-2 text-sm rounded-full border border-[#16a34a] text-[#16a34a] hover:bg-green-50"
                onClick={handleConfirmSaleCert}
                disabled={isUpdatingSaleCert}
              >
                {isUpdatingSaleCert ? "Processing..." : "Confirm"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
